import React, { useEffect, useState, useMemo } from 'react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
} from '@roger/ui-components';
import { Alert, AlertDescription } from '@roger/ui-components';
import { UserPlus, Search, Loader2, AlertTriangle } from 'lucide-react';
import { AddUserDialog } from '../components/rogerdoc/AddUserDialog';
import { AddedUserCard } from '../components/rogerdoc/AddedUserCard';
import { UserCard } from '../components/rogerdoc/UserCard';
import { ResetPasswordDialog } from '../components/rogerdoc/ResetPasswordDialog';
import { DeactivateDialog } from '../components/rogerdoc/DeactivateDialog';
import type { RogerDocUser } from '../services/rogerDocApi';

const PAGE_SIZE = 20;
const alphabet = Array.from({ length: 26 }, (_, i) =>
  String.fromCharCode(65 + i)
);

const RogerDocUsers: React.FC = () => {
  const {
    hasPermission,
    users,
    usersLoading,
    fetchUsers,
    recentlyAddedUser,
    clearRecentlyAddedUser,
  } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
      users: state.users,
      usersLoading: state.usersLoading,
      fetchUsers: state.fetchUsers,
      recentlyAddedUser: state.recentlyAddedUser,
      clearRecentlyAddedUser: state.clearRecentlyAddedUser,
    }))
  );

  // Check if user has permission to access RogerDoc Dashboard
  if (!hasPermission('rogerdoc-dashboard:manage')) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert color='destructive'>
          <AlertTriangle className='h-4 w-4 text-red-600' />
          <AlertDescription className='text-red-800'>
            You don't have permission to access RogerDoc Dashboard features.
            Please contact your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<RogerDocUser | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleResetPassword = (user: RogerDocUser) => {
    setSelectedUser(user);
    setResetPasswordDialogOpen(true);
  };

  const handleDeactivate = (user: RogerDocUser) => {
    setSelectedUser(user);
    setDeactivateDialogOpen(true);
  };

  // Filter users by search term
  const usersBySearch = useMemo(() => {
    if (!users) return [];
    const term = searchTerm.toLowerCase();
    return users.filter(user => {
      const fullName =
        `${user.first_name} ${user.last_name} (${user.username})`.toLowerCase();
      return (
        fullName.includes(term) || user.username?.toLowerCase().includes(term)
      );
    });
  }, [users, searchTerm]);

  // Count users by first letter
  const letterCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    usersBySearch.forEach(user => {
      const fullName =
        `${user.first_name} ${user.last_name} (${user.username})`.trim();
      const firstLetter = fullName.charAt(0).toUpperCase();
      if (firstLetter >= 'A' && firstLetter <= 'Z') {
        counts[firstLetter] = (counts[firstLetter] || 0) + 1;
      }
    });
    return counts;
  }, [usersBySearch]);

  // Filter by selected letter
  const filteredUsers = useMemo(() => {
    if (!selectedLetter) return usersBySearch;
    return usersBySearch.filter(user => {
      const fullName = `${user.first_name} ${user.last_name} (${user.username})`
        .trim()
        .toUpperCase();
      return fullName.startsWith(selectedLetter);
    });
  }, [usersBySearch, selectedLetter]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedLetter, users]);

  // Paginate users
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const totalPages = Math.ceil(filteredUsers.length / PAGE_SIZE);

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
        <div>
          <h1 className='text-3xl font-bold'>Users</h1>
          <p className='text-muted-foreground mt-1'>
            Manage RogerDoc user accounts
          </p>
        </div>
        <Button onClick={() => setAddUserDialogOpen(true)}>
          <UserPlus className='h-4 w-4' />
          Add User
        </Button>
      </div>

      {/* Recently Added User Card */}
      {recentlyAddedUser && (
        <AddedUserCard
          user={recentlyAddedUser}
          onDismiss={clearRecentlyAddedUser}
        />
      )}

      {/* Manage Users Card */}
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between flex-wrap gap-4'>
            <CardTitle>Manage Users ({users?.length ?? 0})</CardTitle>
            <div className='relative w-64'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
              <Input
                placeholder='Search by name or username...'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className='pl-10'
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className='space-y-4'>
          {/* Alphabet Filter */}
          <div className='flex flex-wrap gap-2'>
            <Button
              size='sm'
              variant={selectedLetter ? 'outline' : 'default'}
              onClick={() => setSelectedLetter(null)}
            >
              All ({usersBySearch.length})
            </Button>
            {alphabet.map(letter => (
              <Button
                key={letter}
                size='sm'
                variant={selectedLetter === letter ? 'default' : 'outline'}
                disabled={!letterCounts[letter]}
                onClick={() => setSelectedLetter(letter)}
              >
                {letter} ({letterCounts[letter] || 0})
              </Button>
            ))}
          </div>

          {/* Users Grid */}
          {usersLoading ? (
            <div className='flex items-center justify-center py-12'>
              <Loader2 className='h-8 w-8 animate-spin text-primary' />
            </div>
          ) : paginatedUsers.length > 0 ? (
            <>
              <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3'>
                {paginatedUsers.map(user => (
                  <UserCard
                    key={user.username}
                    firstName={user.first_name}
                    lastName={user.last_name}
                    username={user.username}
                    email={user.email}
                    title={user.clinician_title}
                    onReset={() => handleResetPassword(user)}
                    onDeactivate={() => handleDeactivate(user)}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className='flex items-center justify-between pt-4 border-t'>
                  <div className='text-sm text-muted-foreground'>
                    {(currentPage - 1) * PAGE_SIZE + 1}-
                    {Math.min(currentPage * PAGE_SIZE, filteredUsers.length)} of{' '}
                    {filteredUsers.length} ({users?.length ?? 0} Total)
                  </div>
                  <div className='flex gap-2'>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <div className='flex items-center gap-2'>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(
                          page =>
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1)
                        )
                        .map((page, idx, arr) => (
                          <React.Fragment key={page}>
                            {idx > 0 && arr[idx - 1] !== page - 1 && (
                              <span className='text-muted-foreground'>...</span>
                            )}
                            <Button
                              size='sm'
                              variant={
                                currentPage === page ? 'default' : 'outline'
                              }
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </Button>
                          </React.Fragment>
                        ))}
                    </div>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() =>
                        setCurrentPage(p => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className='text-center py-12 text-muted-foreground'>
              No users found
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <AddUserDialog
        open={addUserDialogOpen}
        onOpenChange={setAddUserDialogOpen}
      />
      {selectedUser && (
        <>
          <ResetPasswordDialog
            open={resetPasswordDialogOpen}
            onOpenChange={setResetPasswordDialogOpen}
            username={selectedUser.username}
            displayName={`${selectedUser.first_name} ${selectedUser.last_name}`}
          />
          <DeactivateDialog
            open={deactivateDialogOpen}
            onOpenChange={setDeactivateDialogOpen}
            username={selectedUser.username}
            displayName={`${selectedUser.first_name} ${selectedUser.last_name}`}
          />
        </>
      )}
    </div>
  );
};

export default RogerDocUsers;
